import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Download } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { transportReportsApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { TransportEnrollmentStatus } from '@/types/transport';
import { formatDate, orDash } from '@/utils/format';
import { BarRow, Money, Stat, daysAgo, humanise, today } from './shared';

type ReportName =
  | 'students'
  | 'routes'
  | 'vehicles'
  | 'drivers'
  | 'trips'
  | 'attendance'
  | 'maintenance'
  | 'incidents';

const TABS: { key: ReportName; label: string; permission: string }[] = [
  { key: 'students', label: 'Students', permission: PERMISSIONS.TRANSPORT_REPORT_VIEW },
  { key: 'routes', label: 'Routes', permission: PERMISSIONS.TRANSPORT_REPORT_VIEW },
  { key: 'vehicles', label: 'Vehicles', permission: PERMISSIONS.TRANSPORT_REPORT_VIEW },
  { key: 'drivers', label: 'Drivers', permission: PERMISSIONS.TRANSPORT_REPORT_VIEW },
  { key: 'trips', label: 'Trips', permission: PERMISSIONS.TRANSPORT_REPORT_VIEW },
  { key: 'attendance', label: 'Attendance', permission: PERMISSIONS.TRANSPORT_REPORT_VIEW },
  { key: 'maintenance', label: 'Maintenance', permission: PERMISSIONS.TRANSPORT_REPORT_VIEW },
  { key: 'incidents', label: 'Incidents', permission: PERMISSIONS.TRANSPORT_REPORT_VIEW },
];

/** The tabs whose figures cover a period rather than a present state. */
const DATED: ReportName[] = ['vehicles', 'trips', 'attendance', 'maintenance', 'incidents'];

const ENROLLMENT_STATUSES: TransportEnrollmentStatus[] = [
  'PENDING',
  'ACTIVE',
  'SUSPENDED',
  'CANCELLED',
  'COMPLETED',
];

/**
 * §34's reports.
 *
 * <p>Each dated report states the range it covers — taken from the payload,
 * because the payload's range is the one the server actually used, and a
 * table of numbers with no dates on it gets printed, filed, and later read as
 * though it described a different term. The default range is the last thirty
 * days rather than all of history: a default that scans every trip a school
 * ever ran is the full-table scan §46 rules out, and defaults are what people
 * actually use.
 *
 * <p>The CSV comes from the same server method as the screen, so the file and
 * the page cannot disagree.
 */
export function TransportReportsPage() {
  const { can } = useAuth();
  const available = TABS.filter((tab) => can(tab.permission));

  const [tab, setTab] = useState<ReportName>(available[0]?.key ?? 'students');
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [status, setStatus] = useState<TransportEnrollmentStatus | ''>('');

  const range = { from, to };
  const statusParam = status === '' ? undefined : status;

  const students = useQuery({
    queryKey: queryKeys.transport.report('students', { status: statusParam }),
    queryFn: () => transportReportsApi.students(undefined, statusParam),
    enabled: tab === 'students',
  });

  const routes = useQuery({
    queryKey: queryKeys.transport.report('routes', undefined),
    queryFn: () => transportReportsApi.routes(),
    enabled: tab === 'routes',
  });

  const vehicles = useQuery({
    queryKey: queryKeys.transport.report('vehicles', range),
    queryFn: () => transportReportsApi.vehicles(from, to),
    enabled: tab === 'vehicles',
  });

  const drivers = useQuery({
    queryKey: queryKeys.transport.report('drivers', undefined),
    queryFn: () => transportReportsApi.drivers(),
    enabled: tab === 'drivers',
  });

  const trips = useQuery({
    queryKey: queryKeys.transport.report('trips', range),
    queryFn: () => transportReportsApi.trips(from, to),
    enabled: tab === 'trips',
  });

  const attendance = useQuery({
    queryKey: queryKeys.transport.report('attendance', range),
    queryFn: () => transportReportsApi.attendance(from, to),
    enabled: tab === 'attendance',
  });

  const maintenance = useQuery({
    queryKey: queryKeys.transport.report('maintenance', range),
    queryFn: () => transportReportsApi.maintenance(from, to),
    enabled: tab === 'maintenance',
  });

  const incidents = useQuery({
    queryKey: queryKeys.transport.report('incidents', range),
    queryFn: () => transportReportsApi.incidents(from, to),
    enabled: tab === 'incidents',
  });

  const queries = {
    students,
    routes,
    vehicles,
    drivers,
    trips,
    attendance,
    maintenance,
    incidents,
  };
  const active = queries[tab];

  function download() {
    switch (tab) {
      case 'students':
        void transportReportsApi.studentsCsv(undefined, statusParam);
        break;
      case 'routes':
        void transportReportsApi.routesCsv();
        break;
      case 'vehicles':
        void transportReportsApi.vehiclesCsv(from, to);
        break;
      case 'drivers':
        void transportReportsApi.driversCsv();
        break;
      case 'trips':
        void transportReportsApi.tripsCsv(from, to);
        break;
      case 'attendance':
        void transportReportsApi.attendanceCsv(from, to);
        break;
      case 'maintenance':
        void transportReportsApi.maintenanceCsv(from, to);
        break;
      case 'incidents':
        void transportReportsApi.incidentsCsv(from, to);
        break;
    }
  }

  const datedReport = DATED.includes(tab);

  return (
    <div>
      <PageHeader
        title="Transport reports"
        description="Totals the database computed, over a range you choose."
        icon={<ClipboardList className="h-5 w-5" aria-hidden="true" />}
        action={
          <Button variant="secondary" onClick={download}>
            <Download className="h-4 w-4" aria-hidden="true" />
            CSV
          </Button>
        }
      />

      <div className="mb-3 flex flex-wrap items-end gap-2">
        {available.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setTab(entry.key)}
            aria-pressed={tab === entry.key}
            className={`border px-3 py-2 text-sm font-semibold ${
              tab === entry.key
                ? 'border-accent bg-accent-soft text-accent-soft-fg'
                : 'border-line bg-bg text-muted'
            }`}
          >
            {entry.label}
          </button>
        ))}

        {tab === 'students' && (
          <span className="ml-auto">
            <SelectField
              label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value as TransportEnrollmentStatus | '')}
            >
              <option value="">All statuses</option>
              {ENROLLMENT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </SelectField>
          </span>
        )}

        {datedReport && (
          <span className="ml-auto flex items-end gap-2">
            <TextField
              label="From"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
            <TextField
              label="To"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </span>
        )}
      </div>

      {active.isError ? (
        <ErrorState error={active.error} onRetry={() => void active.refetch()} />
      ) : active.isFetching ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <>
          {tab === 'students' && students.data !== undefined && (
            <Card className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-line text-left text-[11px] uppercase tracking-[0.04em] text-muted">
                  <tr>
                    <th className="px-5 py-2">Student</th>
                    <th className="px-5 py-2">Pickup route</th>
                    <th className="px-5 py-2">Drop-off route</th>
                    <th className="px-5 py-2">Direction</th>
                    <th className="px-5 py-2 text-right">Monthly fee</th>
                    <th className="px-5 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {students.data.rows.map((row, index) => (
                    <tr key={`${row.studentNumber ?? row.studentName}-${index}`}>
                      <td className="px-5 py-2">
                        <span className="block text-ink">{row.studentName}</span>
                        <span className="block font-mono text-[11px] text-subtle">
                          {orDash(row.studentNumber)}
                        </span>
                      </td>
                      <td className="px-5 py-2 text-muted">{orDash(row.pickupRoute)}</td>
                      <td className="px-5 py-2 text-muted">{orDash(row.dropOffRoute)}</td>
                      <td className="px-5 py-2 text-muted">{humanise(row.direction)}</td>
                      <td className="px-5 py-2 text-right">
                        <Money value={row.monthlyFee} />
                      </td>
                      <td className="px-5 py-2 text-muted">{humanise(row.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {students.data.rows.length === 0 && (
                <p className="py-8 text-center text-sm text-muted">
                  No riders match this filter.
                </p>
              )}
            </Card>
          )}

          {tab === 'routes' && routes.data !== undefined && (
            <Card className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-line text-left text-[11px] uppercase tracking-[0.04em] text-muted">
                  <tr>
                    <th className="px-5 py-2">Route</th>
                    <th className="px-5 py-2">Direction</th>
                    <th className="px-5 py-2 text-right">Capacity</th>
                    <th className="px-5 py-2 text-right">Assigned</th>
                    <th className="px-5 py-2 text-right">Available</th>
                    <th className="px-5 py-2 text-right">Utilization</th>
                    <th className="px-5 py-2">Vehicle</th>
                    <th className="px-5 py-2">Driver</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {routes.data.rows.map((row) => (
                    <tr key={row.code}>
                      <td className="px-5 py-2">
                        <span className="block text-ink">{row.name}</span>
                        <span className="block font-mono text-[11px] text-subtle">{row.code}</span>
                      </td>
                      <td className="px-5 py-2 text-muted">{humanise(row.direction)}</td>
                      <td className="px-5 py-2 text-right tabular-nums">{row.capacity}</td>
                      <td className="px-5 py-2 text-right tabular-nums">{row.assigned}</td>
                      <td className="px-5 py-2 text-right tabular-nums">{row.available}</td>
                      <td className="px-5 py-2 text-right tabular-nums">
                        {row.utilizationPercent >= 100 ? (
                          <span className="text-[color:var(--sms-danger)]">
                            {row.utilizationPercent}%
                          </span>
                        ) : (
                          `${row.utilizationPercent}%`
                        )}
                      </td>
                      <td className="px-5 py-2 text-muted">{orDash(row.vehicle)}</td>
                      <td className="px-5 py-2 text-muted">{orDash(row.driver)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {routes.data.rows.length === 0 && (
                <p className="py-8 text-center text-sm text-muted">No active routes.</p>
              )}
            </Card>
          )}

          {tab === 'vehicles' && vehicles.data !== undefined && (
            <Card className="overflow-x-auto">
              <h2 className="px-5 pt-4 text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                The fleet, {formatDate(vehicles.data.from)} to {formatDate(vehicles.data.to)}
              </h2>
              <p className="px-5 pb-2 pt-0.5 text-xs text-muted">
                Services and spend count what fell inside the range; mileage is the odometer now.
              </p>
              <table className="w-full text-sm">
                <thead className="border-b border-line text-left text-[11px] uppercase tracking-[0.04em] text-muted">
                  <tr>
                    <th className="px-5 py-2">Vehicle</th>
                    <th className="px-5 py-2">Type</th>
                    <th className="px-5 py-2">Status</th>
                    <th className="px-5 py-2 text-right">Seats</th>
                    <th className="px-5 py-2 text-right">Mileage (km)</th>
                    <th className="px-5 py-2 text-right">Services</th>
                    <th className="px-5 py-2 text-right">Maintenance cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {vehicles.data.rows.map((row) => (
                    <tr key={row.vehicle}>
                      <td className="px-5 py-2 text-ink">{row.vehicle}</td>
                      <td className="px-5 py-2 text-muted">
                        {row.type === undefined ? '—' : humanise(row.type)}
                      </td>
                      <td className="px-5 py-2 text-muted">{humanise(row.status)}</td>
                      <td className="px-5 py-2 text-right tabular-nums">{row.seats}</td>
                      <td className="px-5 py-2 text-right tabular-nums">{row.mileageKm}</td>
                      <td className="px-5 py-2 text-right tabular-nums">{row.services}</td>
                      <td className="px-5 py-2 text-right">
                        <Money value={row.maintenanceCost} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {vehicles.data.rows.length === 0 && (
                <p className="py-8 text-center text-sm text-muted">No vehicles on record.</p>
              )}
            </Card>
          )}

          {tab === 'drivers' && drivers.data !== undefined && (
            <Card className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-line text-left text-[11px] uppercase tracking-[0.04em] text-muted">
                  <tr>
                    <th className="px-5 py-2">Driver</th>
                    <th className="px-5 py-2">Role</th>
                    <th className="px-5 py-2">Status</th>
                    <th className="px-5 py-2">Licence</th>
                    <th className="px-5 py-2 text-right">Trips, last 30 days</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {drivers.data.rows.map((row, index) => (
                    <tr key={`${row.employeeNumber ?? row.name}-${index}`}>
                      <td className="px-5 py-2">
                        <span className="block text-ink">{row.name}</span>
                        <span className="block font-mono text-[11px] text-subtle">
                          {orDash(row.employeeNumber)}
                        </span>
                      </td>
                      <td className="px-5 py-2 text-muted">{humanise(row.role)}</td>
                      <td className="px-5 py-2 text-muted">{humanise(row.status)}</td>
                      <td className="px-5 py-2">
                        <span className="block font-mono text-[12px] text-ink">
                          {orDash(row.licenseNumber)}
                        </span>
                        <span className="block text-[11px] text-subtle">
                          {row.licenseExpiry === undefined
                            ? 'No expiry on file'
                            : `Expires ${formatDate(row.licenseExpiry)}`}
                        </span>
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums">{row.tripsLast30Days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {drivers.data.rows.length === 0 && (
                <p className="py-8 text-center text-sm text-muted">No driver profiles yet.</p>
              )}
            </Card>
          )}

          {tab === 'trips' && trips.data !== undefined && (
            <>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <Stat
                  label="Trips"
                  value={trips.data.total}
                  hint={`${formatDate(trips.data.from)} to ${formatDate(trips.data.to)}`}
                />
                <Stat label="Completed" value={trips.data.completed} tone="success" />
                <Stat
                  label="Cancelled"
                  value={trips.data.cancelled}
                  tone={trips.data.cancelled > 0 ? 'danger' : 'neutral'}
                />
                <Stat
                  label="Delayed"
                  value={trips.data.delayed}
                  tone={trips.data.delayed > 0 ? 'warning' : 'neutral'}
                />
                <Stat
                  label="On time"
                  value={
                    trips.data.onTimePercent === null ? '—' : `${trips.data.onTimePercent}%`
                  }
                  hint={
                    // Null, not zero: no closed trips means no share to state.
                    trips.data.onTimePercent === null
                      ? 'Nothing closed in this range'
                      : 'Of trips that closed'
                  }
                />
              </div>
              <Card className="mt-4 p-4">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                  Trips by status
                </h2>
                {trips.data.byStatus.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted">
                    No trips ran in this range.
                  </p>
                ) : (
                  <div className="mt-2">
                    {trips.data.byStatus.map((row) => (
                      <BarRow
                        key={row.label}
                        label={humanise(row.label)}
                        value={row.count}
                        max={Math.max(1, ...trips.data.byStatus.map((entry) => entry.count))}
                      />
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}

          {tab === 'attendance' && attendance.data !== undefined && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <Stat
                  label="Marks"
                  value={attendance.data.totalMarks}
                  hint={`${formatDate(attendance.data.from)} to ${formatDate(attendance.data.to)}`}
                />
                <Stat label="Rode" value={attendance.data.rode} tone="success" />
                <Stat
                  label="Missed"
                  value={attendance.data.missed}
                  tone={attendance.data.missed > 0 ? 'danger' : 'neutral'}
                  hint="Expected and not aboard"
                />
              </div>
              <Card className="mt-4 p-4">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                  Marks by status
                </h2>
                {attendance.data.byStatus.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted">
                    Nobody was marked in this range.
                  </p>
                ) : (
                  <div className="mt-2">
                    {attendance.data.byStatus.map((row) => (
                      <BarRow
                        key={row.label}
                        label={humanise(row.label)}
                        value={row.count}
                        max={Math.max(1, ...attendance.data.byStatus.map((entry) => entry.count))}
                      />
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}

          {tab === 'maintenance' && maintenance.data !== undefined && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Stat
                  label="Spent"
                  value={<Money value={maintenance.data.totalCost} />}
                  hint={`${formatDate(maintenance.data.from)} to ${formatDate(maintenance.data.to)}`}
                />
              </div>
              <Card className="mt-4 p-4">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                  Spend by vehicle
                </h2>
                <p className="mt-0.5 text-xs text-muted">
                  Reporting figures — no payment or ledger entry stands behind them.
                </p>
                {maintenance.data.byVehicle.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted">
                    No services in this range.
                  </p>
                ) : (
                  <div className="mt-2">
                    {maintenance.data.byVehicle.map((row) => (
                      <BarRow
                        key={row.vehicle}
                        label={`${row.vehicle} (${row.services} service${row.services === 1 ? '' : 's'})`}
                        value={row.cost}
                        max={Math.max(1, ...maintenance.data.byVehicle.map((entry) => entry.cost))}
                        suffix={<Money value={row.cost} />}
                      />
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}

          {tab === 'incidents' && incidents.data !== undefined && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Stat
                  label="Incidents"
                  value={incidents.data.total}
                  hint={`${formatDate(incidents.data.from)} to ${formatDate(incidents.data.to)}`}
                />
                <Stat
                  label="Serious or critical"
                  value={incidents.data.seriousOrCritical}
                  tone={incidents.data.seriousOrCritical > 0 ? 'danger' : 'neutral'}
                />
              </div>
              <Card className="mt-4 p-4">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                  Incidents by severity
                </h2>
                {incidents.data.bySeverity.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted">
                    Nothing was reported in this range.
                  </p>
                ) : (
                  <div className="mt-2">
                    {incidents.data.bySeverity.map((row) => (
                      <BarRow
                        key={row.label}
                        label={humanise(row.label)}
                        value={row.count}
                        max={Math.max(1, ...incidents.data.bySeverity.map((entry) => entry.count))}
                      />
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
